import { createApi } from '@reduxjs/toolkit/query/react';
import baseQuery from '../../Shared/API/config';

export interface SalaryAdvance {
  id: number;
  principal: string;
  installmentAmount: string | null;
  reason: string;
  notes: string;
  status: 'open' | 'settled' | 'written_off';
  remainingBalance: string;
  recoveries: Array<{
    amount: string;
    payrollId: number;
    periodMonth: number;
    periodYear: number;
  }>;
  settledAt: string | null;
  created: string;
}

export const EmployeeApi = createApi({
  reducerPath: 'EmployeePayrollApi',
  baseQuery,
  tagTypes: ['employee-payroll', 'banks', 'salary-advance'],
  endpoints: builder => ({
    getEmployees: builder.query<
      any,
      { payGrade?: string; employeeType?: string; graEmployeeType?: string; search?: string } | void
    >({
      query: params => {
        const queryParams = new URLSearchParams();

        if (params?.payGrade) queryParams.append('payGrade', params.payGrade);
        if (params?.employeeType)
          queryParams.append('employeeType', params.employeeType);
        if (params?.graEmployeeType)
          queryParams.append('gra_employee_type', params.graEmployeeType);
        if (params?.search) queryParams.append('q', params.search);

        return `/payroll/employees?${queryParams.toString()}`;
      },
    }),
    getEmployeeById: builder.query<any, string>({
      query: id => `/payroll/employees/${id}`,
      providesTags: ['employee-payroll'],
    }),
    updateEmployee: builder.mutation<any, { id: number; updates: any }>({
      query: ({ id, updates }) => ({
        url: `/payroll/employees/${id}`,
        method: 'PATCH',
        body: updates,
      }),
      invalidatesTags: ['employee-payroll', 'payroll' as any],
    }),
    pausePayroll: builder.mutation<
      any,
      { staffId: number; reason: string; startDate?: string; expectedResumeDate: string; notes?: string }
    >({
      query: ({ staffId, ...body }) => ({
        url: `/payroll/employees/${staffId}/pause-payroll`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['employee-payroll'],
    }),
    resumePayroll: builder.mutation<any, { staffId: number; notes?: string }>({
      query: ({ staffId, ...body }) => ({
        url: `/payroll/employees/${staffId}/resume-payroll`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['employee-payroll'],
    }),
    endPayroll: builder.mutation<any, { staffId: number; stopMonth: number; stopYear: number }>({
      query: ({ staffId, ...body }) => ({
        url: `/payroll/employees/${staffId}/end-payroll`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['employee-payroll'],
    }),
    getEmployeeAdvances: builder.query<{ data: SalaryAdvance[] }, number>({
      query: staffId => `/payroll/employees/${staffId}/advances`,
      providesTags: ['salary-advance'],
    }),
    recordAdvance: builder.mutation<
      SalaryAdvance,
      { staffId: number; principal: string; installmentAmount?: string; reason: string; notes?: string }
    >({
      query: ({ staffId, ...body }) => ({
        url: `/payroll/employees/${staffId}/record-advance`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['salary-advance'],
    }),
    writeOffAdvance: builder.mutation<SalaryAdvance, { advanceId: number; reason: string }>({
      query: ({ advanceId, reason }) => ({
        url: `/payroll/advances/${advanceId}/write-off`,
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: ['salary-advance'],
    }),
    // TODO: FIX ME:: Move banks to its own API as it's not related to employee payroll
    getBanks: builder.query<
      any,
      { payGrade?: string; employeeType?: string; search?: string } | void
    >({
      query: params => {
        const queryParams = new URLSearchParams();
        if (params?.search) queryParams.append('q', params.search);
        return `/payroll/banks?${queryParams.toString()}`;
      },
      providesTags: ['banks'],
    }),
    createBank: builder.mutation<any, any>({
      query: newBanks => ({
        url: '/payroll/banks',
        method: 'POST',
        body: newBanks,
      }),
      invalidatesTags: ['banks', 'employee-payroll'],
    }),
    updateBanks: builder.mutation<any, { id: number; updates: any }>({
      query: ({ id, updates }) => ({
        url: `/payroll/banks/${id}`,
        method: 'PATCH',
        body: updates,
      }),
      invalidatesTags: ['banks', 'employee-payroll'],
    }),
    deleteBanks: builder.mutation<void, number>({
      query: id => ({
        url: `/payroll/banks/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['banks', 'employee-payroll'],
    }),
  }),
});

export const {
  useGetEmployeesQuery,
  useGetEmployeeByIdQuery,
  useUpdateEmployeeMutation,
  usePausePayrollMutation,
  useResumePayrollMutation,
  useEndPayrollMutation,
  useGetEmployeeAdvancesQuery,
  useRecordAdvanceMutation,
  useWriteOffAdvanceMutation,
  useGetBanksQuery,
  useUpdateBanksMutation,
  useCreateBankMutation,
  useDeleteBanksMutation,
} = EmployeeApi;
